import {Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {NgForm} from "@angular/forms";
import {AdsRoute} from "../../../../models/user-ads";
import {PaymentProcessStatus} from "../../../../app.models";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../../../shared/models/apiurl.models";
import {MatDialogRef} from "@angular/material/dialog";
import {CreditCardComponent} from "../credit-card/credit-card.component";
import {LoadingMaskServiceService} from "../../../../components/loading-frame/loading-frame.component";
import {Router} from "@angular/router";
import {timer} from "rxjs";
import {CartService} from "../../../../services/cart.service";

export enum CreditCardType {
  AM="AM",
  DINERS="DINERS",
  MASTER_CARD="MASTER_CARD",
  VISA="VISA"
}
@Component({
    selector: 'app-credit-card-payment',
    templateUrl: './credit-card-payment.component.html',
    styleUrls: ['./credit-card-payment.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CreditCardPaymentComponent implements OnInit {

  @Input() adsId: number;
  @Input() adsRoute: AdsRoute;
  @Input() dialog!: MatDialogRef<CreditCardComponent>;

  // ── Subscription-specific inputs (optional) ───────────────────────────────
  @Input() isSubscription: boolean = false;
  @Input() planId:         number | null = null;   // SubscriptionPlan PK
  @Input() sessionId:      string = '';             // ZCredit session from /initiate/
  @Input() billingPeriod:  string = 'MONTHLY';
  @Input() durationDays:   number = 30;

  // ── Market purchase inputs (SCRUM-20) ─────────────────────────────────────
  @Input() isMarketPurchase: boolean = false;
  @Input() orderId:          number | null = null;   // single MarketOrder PK

  // ── Cart purchase inputs ───────────────────────────────────────────────────
  @Input() isCartPurchase: boolean = false;
  @Input() orderIds:       number[] = [];            // multiple MarketOrder PKs

  // ── SCRUM-266: promote an existing listing ────────────────────────────────
  @Input() promotionId:    number | null = null;     // Promotion PK from /promotions/initiate/

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiUrl.getJWTToken()
    })
  }

  title: string = 'Credit Card Form'; // Change this accordingly
  cardholderName: string = '';
  email: string = '';
  phoneNumber: string = '';
  cardNumber: string = '';
  expirationDate: string = '';
  cvv: string = '';
  numOfPays: number = 1; // Change this accordingly
  creditCardForm: NgForm;
  paymentError = '';

  constructor(private http: HttpClient, private apiUrl: ApiUrl, private loadingMaskServiceService: LoadingMaskServiceService, private router: Router, private cartService: CartService) {
  }

  // Function to format the card number
  formatCardNumber() {
    const numericValue = this.cardNumber.replace(/\D/g, '').slice(0, 16);
    if (numericValue.length > 0) {
      this.cardNumber = numericValue.replace(/(\d{4})/g, '$1 ').trim();
    } else {
      this.cardNumber = '';
    }
  }

  // Function to format the expiration date
  formatExpirationDate() {
    const numericValue = this.expirationDate.replace(/\D/g, '').slice(0, 4);
    if (numericValue.length > 0) {
      this.expirationDate = numericValue.replace(/(\d{2})(\d{0,2})/, '$1/$2').trim();
    } else {
      this.expirationDate = '';
    }
  }


  // Function to check if the expiration date is valid
  isExpirationDateValid() {
    const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
    return regex.test(this.expirationDate);
  }

  // Function to check if the card number is valid
  isCardNumberValid() {
    const regex = /^\d{16}$/;
    return regex.test(this.cardNumber.replace(/\D/g, ''));
  }

  // Function to check if the CVV is valid
  isCvvValid() {
    const regex = /^\d{3}$/;
    return regex.test(this.cvv);
  }

  // Function to handle input changes for card number
  onCardNumberChange() {
    this.formatCardNumber();
  }

  // Function to handle input changes for expiration date
  onExpirationDateChange() {
    this.formatExpirationDate();
  }

  // Function to handle input changes for CVV
  onCvvChange() {
    this.cvv = this.cvv.replace(/\D/g, '').slice(0, 3);
  }
  ngOnInit() {
  }

  getCardType(cardNumber: string): string | null {
    const cleanedCardNumber = cardNumber.replace(/\D/g, '');
    const patterns = {
      "AM": ["34", "37"],
      "DINERS": ["300", "301", "302", "303", "304", "305", "36", "38"],
      "MASTER_CARD": ["51", "52", "53", "54", "55"],
      "VISA": ["4"]
    };
    for (const [cardType, prefixes] of Object.entries(patterns)) {
      for (const prefix of prefixes) {
        if (cleanedCardNumber.startsWith(prefix)) {
          return cardType;
        }
      }
    }
    return null;
  }
  // Function to submit the form
  onSubmit() {
    if (this.isCardNumberValid() && this.isExpirationDateValid() && this.isCvvValid()) {
      this.paymentError = '';
      this.payProcess = PaymentProcessStatus.IN_PROGRESS;

      if (this.isCartPurchase) {
        // ── Cart checkout — POST to /market/cart-confirm/ ─────────────────────
        const cartPayload = {
          order_ids:      this.orderIds,
          session_id:     this.sessionId,
          cardNumber:     this.cardNumber,
          cvv:            this.cvv,
          expirationDate: this.expirationDate,
          cardholderName: this.cardholderName,
        };
        this.http.post<any>(this.apiUrl.confirmCartCheckout(), JSON.stringify(cartPayload), this.options)
          .subscribe({
            next: () => {
              this.loadingMaskServiceService.setOnLoadingMask(false, false);
              this.payProcess = PaymentProcessStatus.DONE;
              this.cartService.clear();
              timer(2000).subscribe(() => { this.dialog.close(); });
            },
            error: error => this.handlePaymentError(error)
          });

      } else if (this.isMarketPurchase) {
        // ── Market product purchase — POST to /market/confirm/ ────────────────
        const mktPayload = {
          order_id:       this.orderId,
          session_id:     this.sessionId,
          cardNumber:     this.cardNumber,
          cvv:            this.cvv,
          expirationDate: this.expirationDate,
          cardholderName: this.cardholderName,
        };
        this.http.post<any>(this.apiUrl.confirmMarketPurchase(), JSON.stringify(mktPayload), this.options)
          .subscribe({
            next: () => {
              this.loadingMaskServiceService.setOnLoadingMask(false, false);
              this.payProcess = PaymentProcessStatus.DONE;
              timer(2000).subscribe(() => {
                this.dialog.close();
              });
            },
            error: error => this.handlePaymentError(error)
          });

      } else if (this.promotionId) {
        // ── SCRUM-266: listing promotion — POST to /business/promotions/confirm/
        const promoPayload = {
          promotion_id:   this.promotionId,
          cardNumber:     this.cardNumber,
          cvv:            this.cvv,
          expirationDate: this.expirationDate,
          cardholderName: this.cardholderName,
          email:          this.email,
          phoneNumber:    this.phoneNumber,
        };
        this.http.post<any>(this.apiUrl.getPromotionConfirmUrl(), JSON.stringify(promoPayload), this.options)
          .subscribe({
            next: () => {
              this.loadingMaskServiceService.setOnLoadingMask(false, false);
              this.payProcess = PaymentProcessStatus.DONE;
              timer(2000).subscribe(() => this.dialog.close('promoted'));
            },
            error: error => this.handlePaymentError(error)
          });

      } else if (this.isSubscription) {
        // ── Subscription payment — POST to /subscriptions/confirm/ ────────────
        const subPayload = {
          plan_id:        this.planId,
          session_id:     this.sessionId,
          cardNumber:     this.cardNumber,
          cvv:            this.cvv,
          expirationDate: this.expirationDate,
          cardholderName: this.cardholderName,
        };
        this.http.post<any>(this.apiUrl.confirmSubscription(), JSON.stringify(subPayload), this.options)
          .subscribe({
            next: () => {
              this.loadingMaskServiceService.setOnLoadingMask(false, false);
              this.payProcess = PaymentProcessStatus.DONE;
              timer(2000).subscribe(() => {
                this.dialog.close();
                this.router.navigate(['/personal/vip-dashboard']);
              });
            },
            error: error => this.handlePaymentError(error)
          });

      } else {
        // ── Ads route payment (original flow) ────────────────────────────────
        const payPayload = {
          cardholderName: this.cardholderName,
          email:          this.email,
          phoneNumber:    this.phoneNumber,
          cardNumber:     this.cardNumber,
          expirationDate: this.expirationDate,
          cvv:            this.cvv,
          numOfPays:      this.numOfPays,
          adsId:          this.adsId,
        };
        this.http.post<any>(this.apiUrl.checkoutAdv(), JSON.stringify(payPayload), this.options)
          .subscribe({
            next: () => {
              this.loadingMaskServiceService.setOnLoadingMask(false, false);
              this.payProcess = PaymentProcessStatus.DONE;
              timer(2000).subscribe(() => {
                this.dialog.close();
                this.router.navigate(['/']);
              });
            },
            error: error => this.handlePaymentError(error)
          });
      }

    } else {
      this.paymentError = 'יש לבדוק את מספר הכרטיס, התוקף וקוד האבטחה.';
    }
  }

  creditCardNumber: string = '';
  expireDate: string = '';


  creditCardType: string | null = CreditCardType.VISA;

  submitForm() {
    // Handle form submission logic here
    console.log('Form submitted!');
  }

  getCardIcon(type: string): string {
    // Replace with your logic to determine the card type and return the corresponding icon URL
    return `assets/icons/${type.toLowerCase()}.png`;
  }

  protected readonly CreditCardType = CreditCardType;
  payProcess: PaymentProcessStatus = PaymentProcessStatus.TODO;

  CheckIfValid(creditCardForm: NgForm) {
    return creditCardForm.status == 'VALID'
  }

  protected readonly PaymentProcessStatus = PaymentProcessStatus;

  isCardType(cardType: CreditCardType) {

    if( this.cardNumber.trim() == '' ){
      return true;
    }
    const vCardType = this.getCardType(this.cardNumber)
    if(cardType == vCardType){
      return true;
    }
    return false;
  }

  closeDialog(): void {
    this.dialog?.close();
  }

  get descriptionLines(): string[] {
    return [...new Set((this.adsRoute?.description || '')
      .split(/\r?\n|\r/)
      .map(line => line.trim())
      .filter(Boolean))];
  }

  get paymentTitle(): string {
    if (this.isSubscription) return 'הצטרפות למסלול';
    if (this.isCartPurchase) return 'תשלום עבור סל הקניות';
    if (this.isMarketPurchase) return 'השלמת רכישה';
    return 'קידום המודעה';
  }

  get durationLabel(): string {
    const days = this.adsRoute?.lifeSpan || this.durationDays;
    return days ? `${days} ימים` : '';
  }

  private handlePaymentError(error: any): void {
    this.payProcess = PaymentProcessStatus.TODO;
    this.loadingMaskServiceService.setOnLoadingMask(false, false);
    this.paymentError = error?.error?.message || error?.error?.detail || error?.error?.errorMessage
      || error?.error?.error || 'התשלום לא הושלם. בדקו את הפרטים ונסו שוב.';
  }
}
